import { ItemSort, type Item } from '@/shared/graphql/router/graphql'
import type { CopyrightOwnerNew } from '@/shared/graphql/legacy/generated'
import { defineStore } from 'pinia'
import { ref } from 'vue'

export type CurrentFolder = {
  id: string
  name: string
  path: string
  createdAt?: number | null
  totalFiles: number
  items: Item[]
  sortBy: ItemSort
  endCursor?: string | null
  hasNextPage: boolean
}

export const DACR_LOCAL_ITEMS_KEY = 'dacrLocalItems'
export const DACR_PENDING_REMOVAL_KEY = 'dacrPendingRemoval'
const DACR_EXPLORER_SORT_KEY = 'dacrExplorerSort'

const readStoredSort = () => {
  const stored = localStorage.getItem(DACR_EXPLORER_SORT_KEY) as ItemSort | null
  return stored && Object.values(ItemSort).includes(stored)
    ? stored
    : ItemSort.NameAsc
}

export const useExplorerStore = defineStore('explorer', () => {
  const currentFolder = ref<CurrentFolder | null>(null)
  const sortBy = ref(readStoredSort())
  const selectedCopyrightOwner = ref<CopyrightOwnerNew>()

  const preventReset = ref(false)

  const setCurrentFolder = (folder: CurrentFolder) => {
    if (preventReset.value && folder.id !== currentFolder.value?.id) return
    currentFolder.value = folder
  }

  const setSort = (sort: ItemSort) => {
    sortBy.value = sort
    localStorage.setItem(DACR_EXPLORER_SORT_KEY, sort)
  }

  const setNewFolder = (folder: {
    id: string
    name: string
    path: string
    createdAt?: number
  }) => {
    preventReset.value = true

    currentFolder.value = {
      id: folder.id,
      name: folder.name,
      path: folder.path,
      createdAt: folder.createdAt,
      totalFiles: 0,
      items: [],
      sortBy: sortBy.value,
      endCursor: null,
      hasNextPage: false,
    }

    setTimeout(() => {
      preventReset.value = false
    }, 1000)
  }

  const appendItems = (items: Item[]) => {
    currentFolder.value?.items.push(...items)
  }

  const appendTempItem = (item: Item) => {
    currentFolder.value?.items.push(item)
  }

  const reset = () => {
    if (!preventReset.value) {
      currentFolder.value = null
    }
  }

  const setCopyrightOwner = (copyrightOwner: CopyrightOwnerNew) => {
    selectedCopyrightOwner.value = copyrightOwner
  }

  return {
    currentFolder,
    setCurrentFolder,
    setNewFolder,
    appendItems,
    appendTempItem,
    reset,
    sortBy,
    setSort,
    selectedCopyrightOwner,
    setCopyrightOwner,
  }
})
